import React from 'react';
import { Typography, Row, Col, Card, Space, Divider } from 'antd';
import { DatabaseOutlined, FileTextOutlined } from '@ant-design/icons';
import '../styles/logo-proposal.css';

const { Title, Paragraph, Text } = Typography;

interface LogoProps {
  style?: React.CSSProperties;
}

// ロゴ案1: モダン・ミニマル（Noto Sans JP）
const Logo1: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-1" style={style}>
    <span className="logo-text">一元帳</span>
  </div>
);

// ロゴ案2: ボールド・インパクト（M PLUS Rounded 1c）
const Logo2: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-2" style={style}>
    <span className="logo-icon">📊</span>
    <span className="logo-text">一元帳</span>
  </div>
);

// ロゴ案3: エレガント・プロフェッショナル（Noto Serif JP）
const Logo3: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-3" style={style}>
    <div className="logo-box">一</div>
    <span className="logo-text">元帳</span>
  </div>
);

// ロゴ案4: テック・モダン（Roboto + Noto Sans JP）
const Logo4: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-4" style={style}>
    <div className="logo-square">
      <span>1</span>
    </div>
    <span className="logo-text">一元帳</span>
  </div>
);

// ロゴ案5: グラデーション・ブランド
const Logo5: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-5" style={style}>
    <div className="logo-gradient-box">
      <DatabaseOutlined style={{ fontSize: 24 }} />
    </div>
    <span className="logo-text">一元帳</span>
  </div>
);

// ロゴ案6: シンプル・タイポグラフィ（Zen Kaku Gothic New）
const Logo6: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-6" style={style}>
    <span className="logo-text">
      <span className="logo-emphasis">一元</span>帳
    </span>
  </div>
);

// ロゴ案7: アイコン統合型
const Logo7: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-7" style={style}>
    <div className="logo-circle">
      <FileTextOutlined style={{ fontSize: 20 }} />
    </div>
    <span className="logo-text">一元帳</span>
  </div>
);

// ロゴ案8: モノグラム型
const Logo8: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-8" style={style}>
    <div className="logo-monogram">
      <span>IGC</span>
    </div>
    <div className="logo-text-vertical">
      <span>一元帳</span>
      <span className="logo-subtitle">ICHIGEN-CHO</span>
    </div>
  </div>
);

// ロゴ案9: モノグラム型 バリエーション1（円形）
const Logo9: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-9" style={style}>
    <div className="logo-monogram-circle">
      <span>一</span>
    </div>
    <div className="logo-text-vertical">
      <span>元帳</span>
      <span className="logo-subtitle">ICHIGEN-CHO</span>
    </div>
  </div>
);

// ロゴ案10: モノグラム型 バリエーション2（六角形）
const Logo10: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-10" style={style}>
    <div className="logo-monogram-hex">
      <span>一</span>
    </div>
    <div className="logo-text-vertical">
      <span>元帳</span>
      <span className="logo-subtitle">ICHIGEN-CHO</span>
    </div>
  </div>
);

// ロゴ案11: モノグラム型 バリエーション3（グラデーション）
const Logo11: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-11" style={style}>
    <div className="logo-monogram-gradient">
      <span>IGC</span>
    </div>
    <div className="logo-text-vertical">
      <span>一元帳</span>
      <span className="logo-subtitle">ICHIGEN-CHO</span>
    </div>
  </div>
);

// ロゴ案12: モノグラム型 バリエーション4（数字強調）
const Logo12: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-12" style={style}>
    <div className="logo-monogram-number">
      <span className="number">1</span>
    </div>
    <div className="logo-text-vertical">
      <span>一元帳</span>
      <span className="logo-subtitle">All-in-One Ledger</span>
    </div>
  </div>
);

// ロゴ案13: モノグラム型 バリエーション5（ミニマル）
const Logo13: React.FC<LogoProps> = ({ style }) => (
  <div className="logo logo-13" style={style}>
    <div className="logo-monogram-minimal">
      <div className="monogram-line"></div>
      <span>一</span>
      <div className="monogram-line"></div>
    </div>
    <div className="logo-text-vertical">
      <span>元帳</span>
    </div>
  </div>
);

export const LogoProposalPage: React.FC = () => {
  const logos = [
    {
      component: Logo1,
      name: 'モダン・ミニマル',
      font: 'Noto Sans JP',
      description: 'シンプルで読みやすい。現代的で親しみやすい印象。',
      characteristics: ['シンプル', '読みやすい', '汎用性高い'],
      color: 'ブルー (#1677ff)',
    },
    {
      component: Logo2,
      name: 'ボールド・インパクト',
      font: 'M PLUS Rounded 1c',
      description: '丸みのある太字で親しみやすく、絵文字アイコンで視覚的に。',
      characteristics: ['親しみやすい', '記憶に残る', 'カジュアル'],
      color: 'グラデーション',
    },
    {
      component: Logo3,
      name: 'エレガント・プロフェッショナル',
      font: 'Noto Serif JP',
      description: '「一」を強調したボックスデザイン。信頼感と専門性。',
      characteristics: ['信頼感', '専門的', 'フォーマル'],
      color: 'ダークブルー (#0958d9)',
    },
    {
      component: Logo4,
      name: 'テック・モダン',
      font: 'Roboto + Noto Sans JP',
      description: '数字「1」を使った幾何学デザイン。先進的な印象。',
      characteristics: ['先進的', 'テクノロジー', 'モダン'],
      color: 'ブルー + グレー',
    },
    {
      component: Logo5,
      name: 'グラデーション・ブランド',
      font: 'Noto Sans JP',
      description: 'データベースアイコン＋グラデーション。ブランドカラー強調。',
      characteristics: ['ブランド統一', '視覚的', '記憶に残る'],
      color: 'グラデーション (#667eea → #764ba2)',
    },
    {
      component: Logo6,
      name: 'シンプル・タイポグラフィ',
      font: 'Zen Kaku Gothic New',
      description: '「一元」を強調したタイポグラフィ。シンプルで力強い。',
      characteristics: ['力強い', 'シンプル', 'モダン'],
      color: 'ダークグレー (#262626)',
    },
    {
      component: Logo7,
      name: 'アイコン統合型',
      font: 'Noto Sans JP',
      description: '円形アイコン＋テキスト。バランスの良いデザイン。',
      characteristics: ['バランス良い', '親しみやすい', 'プロフェッショナル'],
      color: 'ブルー (#1677ff)',
    },
    {
      component: Logo8,
      name: 'モノグラム型',
      font: 'Roboto + Noto Sans JP',
      description: 'IGC（Ichigen-Cho）のモノグラム。縦型レイアウト。',
      characteristics: ['ユニーク', '高級感', '国際的'],
      color: 'ブルー + グレー',
    },
    {
      component: Logo9,
      name: 'モノグラム型 - 円形',
      font: 'Noto Sans JP',
      description: '「一」を円形に配置。元帳を縦型で表示。柔らかい印象。',
      characteristics: ['親しみやすい', '調和的', 'エレガント'],
      color: 'ブルー (#1677ff)',
    },
    {
      component: Logo10,
      name: 'モノグラム型 - 六角形',
      font: 'Noto Sans JP',
      description: '「一」を六角形に配置。技術的で先進的な印象。',
      characteristics: ['先進的', 'ユニーク', 'テクノロジー'],
      color: 'ブルー (#1677ff)',
    },
    {
      component: Logo11,
      name: 'モノグラム型 - グラデーション',
      font: 'Roboto + Noto Sans JP',
      description: 'IGCモノグラムにブランドグラデーション適用。視覚的インパクト。',
      characteristics: ['ブランド統一', '印象的', 'モダン'],
      color: 'グラデーション (#667eea → #764ba2)',
    },
    {
      component: Logo12,
      name: 'モノグラム型 - 数字強調',
      font: 'Roboto + Noto Sans JP',
      description: '数字「1」を大きく強調。All-in-Oneの英訳付き。',
      characteristics: ['直感的', 'グローバル', 'モダン'],
      color: 'ブルー + ゴールド',
    },
    {
      component: Logo13,
      name: 'モノグラム型 - ミニマル',
      font: 'Noto Sans JP',
      description: '「一」をラインで挟んだミニマルデザイン。洗練された印象。',
      characteristics: ['洗練', 'ミニマル', 'エレガント'],
      color: 'ダークグレー (#262626)',
    },
  ];

  return (
    <div className="logo-proposal-page">
      <div className="proposal-header">
        <div className="section-container">
          <Title level={1}>一元帳 ロゴデザイン提案</Title>
          <Paragraph style={{ fontSize: 16, color: 'rgba(255,255,255,0.9)' }}>
            CSS + Google Fontsで構成した13個のロゴ案をご提案します。<br />
            モノグラム型（案8-13）を中心に、各ロゴは実装済みですぐに使用できます。
          </Paragraph>
        </div>
      </div>

      <div className="section-container" style={{ padding: '60px 24px' }}>
        <Row gutter={[32, 32]}>
          {logos.map((logo, index) => (
            <Col xs={24} md={12} lg={12} xl={8} key={index}>
              <Card className="logo-card">
                <div className="logo-preview">
                  <div className="preview-light">
                    <logo.component />
                  </div>
                  <div className="preview-dark">
                    <logo.component />
                  </div>
                </div>

                <Divider />

                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Title level={4} style={{ margin: 0 }}>
                    案{index + 1}: {logo.name}
                  </Title>
                  <Text type="secondary" style={{ fontSize: 13 }}>
                    フォント: {logo.font}
                  </Text>
                  <Paragraph style={{ fontSize: 14, margin: '12px 0' }}>
                    {logo.description}
                  </Paragraph>

                  <div>
                    <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                      特徴
                    </Text>
                    <Space size={[8, 8]} wrap>
                      {logo.characteristics.map((char, idx) => (
                        <span key={idx} className="characteristic-tag">
                          {char}
                        </span>
                      ))}
                    </Space>
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
                      カラー
                    </Text>
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      {logo.color}
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>

        <Divider style={{ margin: '64px 0' }} />

        <div className="usage-examples">
          <Title level={2} style={{ textAlign: 'center', marginBottom: 48 }}>
            使用例
          </Title>

          <Row gutter={[32, 32]}>
            <Col xs={24} lg={12}>
              <Card>
                <Title level={4}>ヘッダーでの使用</Title>
                <div className="usage-demo usage-demo--header">
                  <Logo5 />
                  <Space>
                    <a href="#">機能紹介</a>
                    <a href="#">料金プラン</a>
                    <a href="#">お問い合わせ</a>
                  </Space>
                </div>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card>
                <Title level={4}>ヒーローセクションでの使用</Title>
                <div className="usage-demo usage-demo--hero">
                  <Logo5 style={{ transform: 'scale(1.5)' }} />
                  <Title level={2} style={{ color: 'white', margin: '24px 0 0 0' }}>
                    設備管理を、シンプルに。
                  </Title>
                </div>
              </Card>
            </Col>
          </Row>
        </div>

        <div style={{ marginTop: 64, textAlign: 'center' }}>
          <Card className="implementation-note">
            <Title level={4}>実装について</Title>
            <Paragraph>
              これらのロゴはすべてCSS + Google Fontsで構成されており、
              コンポーネントとして実装済みです。
              選択したロゴをすぐに各ページに適用できます。
            </Paragraph>
            <Paragraph style={{ margin: 0 }}>
              Google Fontsの読み込みは index.html に以下を追加してください:
            </Paragraph>
            <pre style={{ textAlign: 'left', background: '#f5f5f5', padding: 16, borderRadius: 8, marginTop: 16 }}>
{`<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700;900&family=Noto+Serif+JP:wght@400;700;900&family=M+PLUS+Rounded+1c:wght@700;900&family=Zen+Kaku+Gothic+New:wght@400;700;900&family=Roboto:wght@700;900&display=swap" rel="stylesheet">`}
            </pre>
          </Card>
        </div>
      </div>
    </div>
  );
};
